import { useCallback, useEffect, useRef, useState } from "react";
import { VOICE_MAX_SECONDS, createSilenceDetector } from "@demandhub-cms/shared";

export type RecorderProblem = "blocked" | "no_microphone" | "failed" | "silent";

export interface VoiceRecorder {
  supported: boolean;
  recording: boolean;
  /** Whole seconds since recording began. */
  seconds: number;
  /** Live microphone levels while recording (drives the waveform); null otherwise. */
  analyser: AnalyserNode | null;
  start: () => Promise<void>;
  /** Ends the recording and hands the audio to `onRecorded`. */
  stop: () => void;
  /** Ends the recording and throws the audio away. */
  cancel: () => void;
}

const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
const supportsRecording = () => typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== "undefined";

function problemOf(err: unknown): RecorderProblem {
  const name = err instanceof DOMException ? err.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") return "blocked";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "no_microphone";
  return "failed";
}

export function useVoiceRecorder(handlers: { onRecorded: (audio: Blob, seconds: number) => void; onProblem: (problem: RecorderProblem) => void }): VoiceRecorder {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const audioContext = useRef<AudioContext | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const startedAt = useRef(0);
  const discard = useRef(false);
  const latest = useRef(handlers);
  latest.current = handlers;

  const release = useCallback(() => {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    recorder.current = null;
    void audioContext.current?.close().catch(() => undefined);
    audioContext.current = null;
    setAnalyser(null);
    setRecording(false);
    setSeconds(0);
  }, []);

  const stop = useCallback(() => {
    if (recorder.current?.state === "recording") recorder.current.stop();
  }, []);

  const cancel = useCallback(() => {
    discard.current = true;
    stop();
  }, [stop]);

  const start = useCallback(async () => {
    if (recorder.current) return;
    try {
      const media = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      stream.current = media;
      const mimeType = MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m));
      const rec = new MediaRecorder(media, { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 32_000 });
      const chunks: Blob[] = [];
      discard.current = false;
      rec.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
      rec.onerror = () => {
        release();
        latest.current.onProblem("failed");
      };
      rec.onstop = () => {
        const elapsed = (Date.now() - startedAt.current) / 1000;
        const audio = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        const dropped = discard.current;
        release();
        if (!dropped) latest.current.onRecorded(audio, elapsed);
      };
      recorder.current = rec;
      startedAt.current = Date.now();
      rec.start();
      setRecording(true);
      // The level meter is a nicety: if the browser refuses the extra audio graph, recording carries on without it.
      try {
        const ctx = new AudioContext();
        const node = ctx.createAnalyser();
        node.fftSize = 256;
        node.smoothingTimeConstant = 0.75;
        ctx.createMediaStreamSource(media).connect(node);
        audioContext.current = ctx;
        setAnalyser(node);
      } catch {
        setAnalyser(null);
      }
    } catch (err) {
      release();
      latest.current.onProblem(problemOf(err));
    }
  }, [release]);

  useEffect(() => {
    if (!recording) return;
    const tick = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt.current) / 1000);
      setSeconds(elapsed);
      if (elapsed >= VOICE_MAX_SECONDS) stop();
    }, 250);
    return () => window.clearInterval(tick);
  }, [recording, stop]);

  // Hands-free finish: when the person pauses after speaking, the recording ends by itself. Nobody speaking at all ends it too.
  useEffect(() => {
    if (!recording || !analyser) return;
    const detector = createSilenceDetector();
    const buf = new Float32Array(analyser.fftSize);
    const timer = window.setInterval(() => {
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i]! * buf[i]!;
      const verdict = detector.push(Math.sqrt(sum / buf.length), performance.now());
      if (verdict === "stop") stop();
      else if (verdict === "give_up") {
        cancel();
        latest.current.onProblem("silent");
      }
    }, 100);
    return () => window.clearInterval(timer);
  }, [recording, analyser, stop, cancel]);

  useEffect(
    () => () => {
      discard.current = true;
      if (recorder.current?.state === "recording") recorder.current.stop();
      stream.current?.getTracks().forEach((t) => t.stop());
      void audioContext.current?.close().catch(() => undefined);
    },
    []
  );

  return { supported: supportsRecording(), recording, seconds, analyser, start, stop, cancel };
}
